import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Layout } from 'antd';
import useWindowSize from '@iso/lib/hooks/useWindowSize';
import appActions from '@iso/redux/app/actions';
import propertyActions from '@iso/redux/property/actions';
import permissionActions from '@iso/redux/permission/actions';
import siteConfig from '@iso/config/site.config';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import { DashboardContainer, DashboardGlobalStyles } from './Dashboard.styles';
import { loadScript } from '@iso/lib/helpers/googleApiHelper';
import SelectPropertyModal from "@iso/containers/Pages/Property/SelectPropertyModal";
import { useLocation } from "react-router-dom";
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { propertyService } from '@iso/services/property.service';
import { LAYOUT_TOPBAR_OFFSET, LAYOUT_CONTENT_GUTTER } from '@iso/constants/layout';

const { Content, Footer } = Layout;
const { toggleAll } = appActions;
const { changeActiveProperty, setRestoringProperty } = propertyActions;
const { store } = permissionActions;

const styles = {
  layout: { flexDirection: 'row', overflowX: 'hidden' },
  content: {
   padding  :`${LAYOUT_TOPBAR_OFFSET}px ${LAYOUT_CONTENT_GUTTER}px ${LAYOUT_CONTENT_GUTTER}px ${LAYOUT_CONTENT_GUTTER}px`,
    flexShrink: '0',
    background: 'rgba(143, 146, 161, 0.05)',
    position: 'relative',
  },
  footer: {
    backgroundColor:"#ffffff !important",
    borderTop: "1px solid white !important",
    textAlign: "center",
  },
};

export default function PrivateLayout(props) {
  const dispatch = useDispatch();
  const appHeight = useSelector(state => state.App.height);
  const { width, height } = useWindowSize();

  const permissions = useSelector(state => state.permission.permissions);
  const currentUser = useSelector((state) => state.user.user);

  const activePropertyId = useSelector(
    (state) => state.property.activePropertyId
  );
  const location = useLocation();

  const [visibleSelectProperty, setVisiblitySelectProperty] = React.useState(false);
  const restoringProperty = useSelector((state) => state.property.restoringProperty);
  const isManageProperties = location.pathname.includes("manage-properties");

  React.useEffect(() => {
    if (!isManageProperties && !activePropertyId && !restoringProperty) {
      setVisiblitySelectProperty(true);
    }else {
      setVisiblitySelectProperty(false);
    }
  }, [activePropertyId, location.pathname, restoringProperty]);

  const cancelSelectPropertyModal = () => {
    setVisiblitySelectProperty(false);
  };

  React.useEffect(() => {
    dispatch(toggleAll(width, height));
  }, [width, height, dispatch]);

  React.useEffect(() => {
    const cachedProperty = commonHelper.getActiveProperty();
    if (cachedProperty) {
      // Rehydrate from this device's cache only; the choice is already
      // persisted server-side, so don't re-write it on every page load.
      dispatch(changeActiveProperty(cachedProperty, false));
      dispatch(setRestoringProperty(false));
    } else {
      // Fetch the FULL property list (not a paginated page) so the persisted
      // active property is always found — getProperties() returns only the
      // first page, so an active property sorted beyond it would be missed and
      // wrongly overwritten by the fallback below.
      propertyService.getAllProperties().then((res) => {
        const list = res?.properties?.data || res?.properties || [];
        if (list.length > 0) {
          const persistedActive = list.find(p => p.is_actived === 1);
          const restored = persistedActive || list[0];
          localStorage.setItem('active_property', JSON.stringify(restored));
          // If the user has no persisted active property (e.g. assigned a
          // property but never activated one), the auto-selected first
          // property becomes their persistent choice — write it back so it
          // sticks on the next login / device. When one is already persisted
          // we just rehydrate without re-writing.
          dispatch(changeActiveProperty(restored, !persistedActive));
        }
      }).catch((err) => {
        console.error('Failed to restore active property:', err);
      }).finally(() => {
        dispatch(setRestoringProperty(false));
      });
    }

    if(permissions.length == 0){
      dispatch(store());
    }

    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY_ADDRESS}&libraries=places`
    );

  }, [dispatch]);

  const isMap = window.location.pathname.split('/').slice(-1)[0] == "map" || window.location.pathname.split('/').slice(-1)[0] == "share-map";

  return (
    <DashboardContainer>
      <DashboardGlobalStyles />
      <Layout style={{ height: height }}>
        <Topbar />
        <Layout style={styles.layout}>
          { !currentUser.is_public_view_user &&  <Sidebar />}
          <Layout
            className="isoContentMainLayout"
            style={{
              height: appHeight,
            }}
          >

            <SelectPropertyModal
              visibleSelectProperty={visibleSelectProperty}
              cancelSelectPropertyModal={cancelSelectPropertyModal}
            />

            <Content className="isomorphicContent" style={styles.content}>
              {props.children}
            </Content>
            {!isMap &&<Footer style={styles.footer}>{siteConfig.footerText}</Footer>}
          </Layout>
        </Layout>
      </Layout>
    </DashboardContainer>
  );
}
